import { currentOrdersScopeKey, useAccountStore } from '@/stores/accountStore';
import { useAuthStore } from '@/stores/authStore';
import type { KalshiOrder } from '@/types/kalshi';

const NO_ORDERS: readonly KalshiOrder[] = [];

/**
 * True when the Kalshi open-orders list is the WHOLE desk rather than the
 * viewer's own: an admin on an org key reads (and may cancel) every trader's
 * and the Vanilla MM bot's orders, while their positions stay their own
 * (#956 review, #961). Fails closed — `keyMode` is null until the credential
 * check lands, and the relay decides scope per request — so only a key known
 * to be the admin's own counts as theirs. Non-admins are always scoped to
 * their own orders by the relay.
 */
export function useOrdersAreDeskWide(): boolean {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const keyMode = useAccountStore((s) => s.keyMode);
  return isAdmin && keyMode !== 'user';
}

/** A retained list is not proof for a changed account or credential generation. */
export function useCloseOrdersAuthority(): boolean {
  useAuthStore((s) => s.user);
  useAccountStore((s) => s.keyMode);
  useAccountStore((s) => s.status);
  const observed = useAccountStore((s) => s.ordersScopeKey);
  const current = currentOrdersScopeKey();
  return current !== null && observed === current;
}

/**
 * The open orders that may be counted as closing the viewer's positions
 * (see workingCloses): all of them, or none when the list is desk-wide —
 * another trader's resting order never closes this viewer's position.
 * Stable empty array, safe as a memo dependency.
 */
export function useOrdersForCloseCount(): readonly KalshiOrder[] {
  const deskWide = useOrdersAreDeskWide();
  const authoritative = useCloseOrdersAuthority();
  const openOrders = useAccountStore((s) => s.openOrders);
  return deskWide || !authoritative ? NO_ORDERS : openOrders;
}
